import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './MagicLinkCallback.css'; // Reuse existing sleek styling
import Logo from '../../../components/Logo/Logo';

import { applyHandoffContinuation, getPendingHandoffToken } from '../../edu-landing/handoffContinuation';
import { continuePendingCheckoutOrChat } from '../continueAfterSignIn';
import { oauthRedirectUri } from '../oauthRedirect';
import {
  clearPasswordChangeSignIn,
  recordPasswordChangeSignIn,
} from '../passwordChangeSignIn';

const LinkedInCallback = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const returnedState = searchParams.get('state');
  const errorParam = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');
  
  const navigate = useNavigate();
  const { loginWithLinkedIn } = useAuth();
  
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing verification...');
  const [isVisible, setIsVisible] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    // AuthProvider builds a new loginWithLinkedIn on every render, and that
    // function's first setState re-renders the provider. The effect would
    // otherwise run again, fail the already-consumed state check, and show
    // "Verification Failed" while the first sign-in still succeeds.
    if (startedRef.current) return;
    startedRef.current = true;

    setIsVisible(true);
    
    const verifySSO = async () => {
      // 1. Check for error params sent by LinkedIn (e.g. user cancelled)
      if (errorParam) {
        setError(`LinkedIn auth error: ${errorDescription || errorParam}. Please try again.`);
        return;
      }

      if (!code) {
        setError('No authorization code was returned from LinkedIn. Please try signing in again.');
        return;
      }

      // 2. CSRF Protection: Verify state token matches what was stored in sessionStorage
      const savedState = sessionStorage.getItem('linkedin_oauth_state');
      sessionStorage.removeItem('linkedin_oauth_state'); // Consume immediately

      if (!returnedState || returnedState !== savedState) {
        setError('Security check failed: OAuth state mismatch. This might be a cross-site request forgery attempt. Please try signing in again.');
        return;
      }
      
      try {
        setStatus('Verifying your LinkedIn credentials and logging in...');
        // Must equal the redirect_uri Login.js sent to LinkedIn, or the code exchange fails.
        const redirectUri = oauthRedirectUri('linkedin');
        const pendingHandoffToken = getPendingHandoffToken();

        recordPasswordChangeSignIn({ source: 'linkedin' });
        const result = await loginWithLinkedIn(code, redirectUri, true, pendingHandoffToken);

        if (result.success) {
          applyHandoffContinuation(result);
          if (result.user?.must_change_password) {
            setStatus('Authentication successful! Logging you in...');
            navigate('/set-password', { replace: true });
            return;
          }
          clearPasswordChangeSignIn();
          setStatus('Authentication successful! Logging you in...');
          setTimeout(() => {
            continuePendingCheckoutOrChat({ navigate, user: result.user });
          }, 800);
        } else {
          clearPasswordChangeSignIn();
          setError(result.error || 'LinkedIn SSO verification failed. Please try login again.');
        }
      } catch (err) {
        clearPasswordChangeSignIn();
        console.error('LinkedIn Callback authentication failure:', err);
        setError('An unexpected error occurred during LinkedIn authentication. Please try again.');
      }
    };

    verifySSO();
  }, [code, returnedState, errorParam, errorDescription, loginWithLinkedIn, navigate]);

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-overlay"></div>
      </div>
      
      <div className="login-container">
        <div className={`login-content ${isVisible ? 'fade-in-up' : ''}`} style={{ textAlign: 'center', padding: '50px 40px' }}>
          <div className="login-header" style={{ justifyContent: 'center', marginBottom: '30px' }}>
            <div className="login-logo">
              <Logo onClick={() => navigate('/')} />
            </div>
          </div>
          
          <div className="callback-card-content">
            {error ? (
              <div className="error-state">
                <div className="error-icon" style={{ 
                  color: 'var(--color-status-error)', 
                  fontSize: '48px', 
                  marginBottom: '20px' 
                }}>
                  ⚠
                </div>
                <h2 style={{ color: 'var(--color-text-primary)', marginBottom: '12px', fontSize: '24px', fontWeight: '700' }}>
                  Verification Failed
                </h2>
                <p style={{ color: 'var(--color-text-secondary)', marginBottom: '32px', fontSize: '14px', lineHeight: '1.5' }}>
                  {error}
                </p>
                <button 
                  onClick={() => navigate('/login')}
                  className="login-button"
                  style={{ width: '100%' }}
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <div className="loading-state">
                <div className="premium-spinner-container">
                  <div className="outer-pulse-ring"></div>
                  <div className="inner-rotating-ring"></div>
                </div>
                <h3 style={{ color: 'var(--color-text-primary)', fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                  LinkedIn SSO Authentication
                </h3>
                <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                  {status}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInCallback;
